import { 
  Modal, 
  Box, 
  Typography, 
  Button, 
  IconButton,
  Stack,
  useTheme,
  useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { CustomModalProps, ModalButton } from '../interfaces/modal';

function CustomModal({
  open,
  onClose,
  title,
  content,
  buttons = [],
  maxWidth = 'sm',
  showCloseIcon = true,
}: CustomModalProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  const modalStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    bgcolor: 'background.paper',
    boxShadow: 24,
    borderRadius: '8px',
    p: { xs: 2, sm: 2.5 },
    pt: { xs: 1.5, sm: 2 },
    pb: { xs: 1, sm: 1 },
    border: `1px solid ${theme.palette.divider}`,
    maxHeight: '90vh',
    overflow: 'auto',
    minWidth: { xs: '280px', sm: '350px' },
    minHeight: { xs: '170px', sm: '190px' },
    m: { xs: 2, sm: 0 },
  };

  const cancelButtonStyle = {
    color: theme.palette.text.secondary,
    borderColor: theme.palette.divider,
    fontWeight: 400,
    textTransform: 'uppercase',
    px: 3,
  };

  const actionButtonStyle = {
    fontWeight: 500,
    textTransform: 'uppercase',
    px: 3,
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="modal-title"
      aria-describedby="modal-description"
    >
      <Box sx={{
        ...modalStyle,
        width: 'auto',
        maxWidth: maxWidth !== 'xs' ? { xs: 'calc(100% - 32px)', sm: '420px' } : { xs: 'calc(100% - 32px)', sm: '380px' },
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
          {title && (
            <Typography 
              id="modal-title" 
              variant="h6" 
              component="h2"
              sx={{ 
                color: '#4db0b1',
                fontSize: '1.125rem',
                fontWeight: 500
              }}
            >
              {title}
            </Typography>
          )}
          
          {showCloseIcon && (
            <IconButton
              aria-label="close"
              onClick={onClose}
              sx={{ ml: 'auto' }}
              size="small"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
        </Box>
        
        <Box id="modal-description" sx={{ mb: 3 }}>
          {content}
        </Box>
        
        {buttons.length > 0 && (
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={1} 
            justifyContent="flex-end"
            sx={{ mb: 0.5 }}
          >
            {buttons.map((button: ModalButton) => (
              <Button
                key={button.id}
                variant={button.variant || (button.isCancel ? 'outlined' : 'contained')}
                color={button.color || (button.isCancel ? 'inherit' : 'primary')}
                onClick={button.onClick}
                autoFocus={button.autoFocus}
                disableElevation
                fullWidth={isMobile}
                size="small"
                sx={{
                  ...(button.isCancel ? cancelButtonStyle : actionButtonStyle),
                  py: { xs: 0.75, sm: 0.75 },
                  fontSize: '0.875rem',
                  width: { xs: '100%', sm: 'auto' },
                }}
              >
                {button.label}
              </Button>
            ))}
          </Stack>
        )}
      </Box>
    </Modal>
  );
}

export default CustomModal;